import "./App.css";
import React, { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AppProvider } from "./context/AppContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Home from "./pages/Home";

const CarDetail = lazy(() => import("./pages/CarDetail"));
const RentSlug = lazy(() => import("./pages/RentSlug"));
const ModelPage = lazy(() => import("./pages/ModelPage"));
const AllFleetPage = lazy(() => import("./pages/AllFleetPage"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminCars = lazy(() => import("./pages/admin/AdminCars"));
const AdminRequests = lazy(() => import("./pages/admin/AdminRequests"));
const AdminAvailability = lazy(() => import("./pages/admin/AdminAvailability"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminSales = lazy(() => import("./pages/admin/AdminSales"));
const LegalPage = lazy(() => import("./pages/LegalPage"));

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#070709]">
        <div className="w-10 h-10 border-2 border-teal-300 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <AdminLogin />;
  return children;
}

function detectLangPrefix() {
  const n = ((typeof navigator !== "undefined" && (navigator.language || navigator.userLanguage)) || "en").toLowerCase();
  if (n.startsWith("tr")) return "/tr";
  if (n.startsWith("ru")) return "/ru";
  return "/eng";
}

function RootLangRedirect() {
  const { search } = useLocation();
  return <Navigate to={`${detectLangPrefix()}${search}`} replace />;
}

function LegacyToEng() {
  const { pathname, search } = useLocation();
  return <Navigate to={`/eng${pathname}${search}`} replace />;
}

function RedirectToNotFound() {
  const { pathname } = useLocation();
  const prefix = pathname.startsWith("/tr")
    ? "/tr"
    : pathname.startsWith("/ru")
      ? "/ru"
      : "/eng";

  return <Navigate to={`${prefix}/not-found`} replace />;
}

const LANG_PREFIXES = ["/eng", "/tr", "/ru"];

const localizedRoutes = (p) => [
  <Route key={`${p}-home`} path={p} element={<Home />} />,
  <Route key={`${p}-car`} path={`${p}/car/:id`} element={<CarDetail />} />,
  <Route key={`${p}-car-detail`} path={`${p}/rent/:brand/:model/*`} element={<CarDetail />} />,
  <Route key={`${p}-rent`} path={`${p}/rent/:slug`} element={<RentSlug />} />,
  <Route key={`${p}-fleet`} path={`${p}/rent-a-luxury-car-in-dubai`} element={<AllFleetPage />} />,
  <Route key={`${p}-ty`} path={`${p}/thank-you`} element={<ThankYou />} />,
  <Route key={`${p}-privacy`} path={`${p}/privacy-policy`} element={<LegalPage type="privacy" />} />,
  <Route key={`${p}-rental`} path={`${p}/rental-conditions`} element={<LegalPage type="rental" />} />,
  <Route key={`${p}-nf`} path={`${p}/not-found`} element={<NotFound />} />,
  <Route key={`${p}-slug`} path={`${p}/:slug`} element={<ModelPage />} />,
];

function RouteLoader() {
  return (
    <main
      className="flex min-h-screen items-center justify-center bg-[#F4F1EB]"
      data-testid="route-loader"
    >
      <div className="h-9 w-9 animate-spin border-2 border-teal-800 border-t-transparent" />
    </main>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppProvider>
          <AuthProvider>
            <Suspense fallback={<RouteLoader />}>
              <Routes>
                <Route path="/" element={<RootLangRedirect />} />
                {LANG_PREFIXES.flatMap(localizedRoutes)}
                <Route path="/car/:id" element={<LegacyToEng />} />
                <Route path="/rent/:slug" element={<LegacyToEng />} />
                <Route path="/rent-a-luxury-car-in-dubai" element={<LegacyToEng />} />
                <Route path="/thank-you" element={<LegacyToEng />} />
                <Route path="/not-found" element={<LegacyToEng />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="cars" element={<AdminCars />} />
                  <Route path="availability" element={<AdminAvailability />} />
                  <Route path="requests" element={<AdminRequests />} />
                  <Route path="sales" element={<AdminSales />} />
                  <Route path="settings" element={<AdminSettings />} />
                </Route>
                <Route path="/:slug" element={<LegacyToEng />} />
                <Route path="*" element={<RedirectToNotFound />} />
              </Routes>
            </Suspense>
            <Toaster position="top-right" richColors theme="dark" />
          </AuthProvider>
        </AppProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
